import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Main from "../../../../layout/main/Main";
import { Space, Radio, Dropdown, Menu, Modal } from "antd";
import {
	CloseCircleOutlined,
	InfoCircleOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import {
	ActionButton,
	AntdRadioButton,
	FilterActions,
	FilterContainer,
	Filters,
	RadioOptions,
	TableContainer,
} from "./styles";
import LoadingHeader from "./components/LoadingHeader";
import Image from "../../../../common/image/Image";
import { FaImages } from "react-icons/fa";
import { COLORS, Typo } from "../../../../layout/theme";
import DropShadow from "../../../../common/drop-shadow/DropShadow";
import SignInTemplate from "../../../../layout/sign-in-template/SignInTemplate";
import debounce from "lodash/debounce";
import { BsThreeDotsVertical } from "react-icons/bs";
import AntdSearch from "../../../../common/antd-search/AntdSearch";
import {
	get_chains,
	get_chains_header_info,
	update_chain_state,
} from "../../../../../api/endpoints/chain";
import ChainTable from "./components/chain-table/ChainTable";
import PrimaryButton from "../../../../common/antd-buttons/PrimaryButton";

const { confirm } = Modal;

export default function ChainList() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");
	const [headerInfo, setHeaderInfo] = useState(null);
	const [data, setData] = useState(null);
	const [state, setState] = useState("ACTIVE");
	const [key, setKey] = useState("null");
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const columns = [
		{
			title: (algo) => {
				return <FaImages style={{ color: COLORS.White }} />;
			},
			width: 150,
			dataIndex: "photoUrl",
			render: (value, record, index) => (
				<Image width={40} height={40} src={value} alt={record.title} />
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Nombre</div>,
			dataIndex: "title",
			render: (value, record) => (
				<Link
					to={`/admin/chains/${key.id}?uri=${uri}`}
					onMouseOver={() => setKey(record)}
				>
					<Typo type="muted" fontWeight={600} fontSize={14}>
						{value}
					</Typo>
				</Link>
			),
		},
		{
			title: () => (
				<div style={{ color: COLORS.White }}>Farmacias asociadas</div>
			),
			dataIndex: "clients",
			render: (value, record) => (
				<Typo type="muted" fontWeight={600} fontSize={14}>
					{record.clients?.length ?? []}
				</Typo>
			),
		},
		{
			title: "",
			key: "id",
			dataIndex: "id",
			width: 10,
			render: (f, r, i) => (
				<>
					<Dropdown
						overlay={r.isActive ? menu : menuInactive}
						placement="bottomRight"
					>
						<ActionButton key={`${f}`} onMouseOver={() => setKey(r)}>
							<BsThreeDotsVertical />
						</ActionButton>
					</Dropdown>
				</>
			),
		},
	];

	const menu = (
		<Menu
			items={[
				{
					key: "1",
					label: (
						<Link to={`/admin/chains/${key.id}?uri=${uri}`}>
							<Typo type="secondary">Editar</Typo>
						</Link>
					),
				},
				{
					key: "2",
					label: <Typo onClick={() => handleDelete(key)}>Desactivar</Typo>,
				},
			]}
		/>
	);

	const menuInactive = (
		<Menu
			items={[
				{
					key: "1",
					label: (
						<Link to={`/admin/chains/${key.id}?uri=${uri}`}>
							<Typo type="secondary">Editar</Typo>
						</Link>
					),
				},
				{
					key: "2",
					label: <Typo onClick={() => handleActivate(key)}>Reactivar</Typo>,
				},
			]}
		/>
	);

	const fetch_chains = async (search = "", skipValue = null) => {
		setLoading(true);
		try {
			const res = await get_chains(search, state);

			setData(res);
		} catch (err) {
			console.log(err);
		} finally {
			setLoading(false);
		}
	};

	const fetch_chain_header = async () => {
		const res = await get_chains_header_info();
		setHeaderInfo(res);
	};

	useEffect(() => {
		fetch_chain_header();
	}, [state]);

	useEffect(() => {
		fetch_chains();
	}, [state]);

	useEffect(() => {
		return () => {
			debouncedEventHandler.cancel();
		};
	}, []);

	const eventHandler = (e) => {
		setSearchTerm(e.target.value);
		fetch_chains(e.target.value);
	};

	const debouncedEventHandler = useMemo(() => debounce(eventHandler, 250), []);

	const handleDelete = (dist) => {
		confirm({
			title: "¿Seguro que quiere desactivar esta cadena?",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content: "La desactivación de la cadena se aplicará de inmediato",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => {
				handleDeleteSecondWarning(dist);
			},
		});
	};

	const handleDeleteSecondWarning = (dist) => {
		confirm({
			title:
				"Tenga en cuenta que al continuar con esta acción la cadena quedará inaccesible en el sistema",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content: "Desea proceder de todas maneras?",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => {
				deleteChain(dist);
			},
		});
	};

	const deleteChain = async (dist) => {
		setLoading(true);
		const _state = { id: dist.id, state: "INACTIVE" };
		await update_chain_state(dist.id, _state);
		fetch_chains();
		fetch_chain_header();
		setLoading(false);
	};

	const handleActivate = (dist) => {
		confirm({
			title: "¿Seguro que quiere reactivar esta cadena?",
			icon: <InfoCircleOutlined style={{ color: "blue" }} />,
			content: "La reactivación de la cadena se aplicará de inmediato",
			okText: "Sí",
			okType: "default",
			cancelText: "No",
			onOk: () => {
				activateTemplate(dist);
			},
		});
	};

	const activateTemplate = async (dist) => {
		setLoading(true);
		const _state = { id: dist.id, state: "ACTIVE" };
		await update_chain_state(dist.id, _state);
		fetch_chains();
		fetch_chain_header();
		setLoading(false);
		// }
	};

	// const fetch_export_chains = async () => {
	// 	setLoading(true);
	// 	try {
	// 		const res = await export_chains();
	// 		const href = URL.createObjectURL(res);

	// 		const link = document.createElement("a");
	// 		link.href = href;

	// 		const fileName = "ChainsList_" + moment().format("YYYYMMDD") + ".xlsx";
	// 		link.setAttribute("download", fileName);
	// 		document.body.appendChild(link);
	// 		link.click();

	// 		document.body.removeChild(link);
	// 		URL.revokeObjectURL(href);
	// 	} catch (error) {
	// 		console.log(error);
	// 	} finally {
	// 		setLoading(false);
	// 	}
	// };

	return (
		<Main>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/" + uri}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>Cadenas</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>

			<Main.Body>
				<Filters>
					{!headerInfo ? (
						<LoadingHeader />
					) : (
						<FilterContainer>
							<AntdSearch
								width={400}
								allowClear
								placeholder="Buscar cadena"
								onChange={debouncedEventHandler}
								style={{ height: 35, borderRadius: 12 }}
							/>
							<RadioOptions>
								<Radio.Group
									value={state}
									onChange={(e) => setState(e.target.value)}
								>
									<AntdRadioButton value="ACTIVE">
										Activo ({headerInfo?.totalActive})
									</AntdRadioButton>
									<AntdRadioButton value="INACTIVE">
										Inactivo ({headerInfo?.totalInactive})
									</AntdRadioButton>
								</Radio.Group>
							</RadioOptions>
							<FilterActions>
								<PrimaryButton
									type="primary"
									bg="Primary"
									color="White"
									icon={<PlusOutlined />}
									onClick={() => navigate("/admin/chains/create?uri=" + uri)}
									style={{ width: "200px", fontWeight: "400" }}
								>
									Crear cadena
								</PrimaryButton>
							</FilterActions>
						</FilterContainer>
					)}
				</Filters>
				<TableContainer>
					<ChainTable
						data={data}
						columns={columns}
						loading={loading}
						isHeaderShow={true}
					/>
				</TableContainer>
			</Main.Body>
		</Main>
	);
}
